import { useEffect, useState } from 'react'
import type { PublicConfig } from '@hedgehog/shared'
import { api } from '@/lib/api'

/**
 * Shown before /config answers and used in <title> tags, which render before the config loads.
 * Keep in sync with the `brand` value the API serves and with index.html.
 */
export const BRAND_FALLBACK = 'Hedgehog'

let pending: Promise<PublicConfig> | null = null
let loaded: PublicConfig | null = null

export function loadConfig(): Promise<PublicConfig> {
  pending ??= api<PublicConfig>('/config').then(
    (config) => (loaded = config),
    (err: unknown) => {
      pending = null // allow a later retry
      throw err
    },
  )
  return pending
}

export type ConfigState =
  | { status: 'loading'; config: null }
  | { status: 'ready'; config: PublicConfig }
  | { status: 'error'; config: null }

/** Public config shared by every marketing component. Fetched once per page load. */
export function usePublicConfig(): ConfigState {
  const [state, setState] = useState<ConfigState>(() =>
    loaded ? { status: 'ready', config: loaded } : { status: 'loading', config: null },
  )
  useEffect(() => {
    if (state.status === 'ready') return
    let live = true
    loadConfig().then(
      (config) => live && setState({ status: 'ready', config }),
      () => live && setState({ status: 'error', config: null }),
    )
    return () => {
      live = false
    }
  }, [state.status])
  return state
}

